import { Checkbox, FormControlLabel, Tooltip } from '@mui/material';
import { Chain, ChainID, ChainPath } from '@ankr.com/chains-list';
import { useCallback } from 'react';
import { CheckboxClasses } from '@mui/material/Checkbox/checkboxClasses';

import { EndpointGroup } from 'modules/endpoints/types';
import { getDisabledChainTooltipText } from 'modules/chains/utils/getDisabledChainTooltipText';

import { getCustomLabelForChainsCornerCases } from '../../utils/getCustomLabelForChainsCornerCases';
import { getChainName } from '../../utils/getChainName';
import { getPathFromSubchain } from './useChainTypeSelectorWithSubchains';

interface ISubchainSelectorProps {
  isLoading?: boolean;
  classes?: Partial<CheckboxClasses>;
  checkBoxClassName?: string;
  subChain: Chain;
  endpoints: EndpointGroup[];
  selectedChainPaths: ChainPath[];
  shouldAvoidChainNameFormatting?: boolean;
  handleChangeChild: (path: ChainPath) => void;
  isDisabled?: boolean;
}

/**
 * Component is used in projects sidebar and chain step in project flow to display subchain checkboxes
 * */
export const SubchainSelector = ({
  checkBoxClassName,
  classes,
  endpoints,
  handleChangeChild,
  isDisabled,
  isLoading,
  selectedChainPaths,
  shouldAvoidChainNameFormatting,
  subChain,
}: ISubchainSelectorProps) => {
  const currentEndpoint = endpoints.find(endpoint =>
    endpoint.chains.some(endpointChain => endpointChain.id === subChain.id),
  );

  const path = getPathFromSubchain(subChain);

  const isAlloraMainnetSubchain = subChain.chainExtends === ChainID.ALLORA;
  const isAlloraTestnetSubchain =
    subChain.chainExtends === ChainID.ALLORA_TESTNET;
  const isKavaTestnetSubchain = subChain.chainExtends === ChainID.KAVA_TESTNET;
  const isKavaMainnetSubchain = subChain.chainExtends === ChainID.KAVA;
  const isSeiSubchain = subChain.chainExtends === ChainID.SEI;
  const isSuiSubchain = subChain.chainExtends === ChainID.SUI;
  const isTacMainnetSubchain = subChain.chainExtends === ChainID.TAC;
  const isTacTestnetSubchain =
    subChain.chainExtends === ChainID.TAC_TESTNET_SPB;
  const isFlareCostonSubchain = subChain.chainExtends === ChainID.FLARE_COSTON;
  const isFlareCoston2Subchain =
    subChain.chainExtends === ChainID.FLARE_COSTON2;
  const isFlareSongbirdSubchain =
    subChain.chainExtends === ChainID.FLARE_SONGBIRD;

  const label =
    currentEndpoint &&
    !isAlloraMainnetSubchain &&
    !isAlloraTestnetSubchain &&
    !isFlareCostonSubchain &&
    !isFlareCoston2Subchain &&
    !isFlareSongbirdSubchain &&
    !isKavaMainnetSubchain &&
    !isKavaTestnetSubchain &&
    !isSeiSubchain &&
    !isSuiSubchain &&
    !isTacMainnetSubchain &&
    !isTacTestnetSubchain
      ? getCustomLabelForChainsCornerCases({
          chainId: subChain.id,
          label: currentEndpoint.name,
        })
      : getChainName(subChain, shouldAvoidChainNameFormatting);

  const onChangeChild = useCallback(
    () => handleChangeChild(path),
    [handleChangeChild, path],
  );

  const isChecked = selectedChainPaths.includes(path);
  const disabled = isChecked && isDisabled;

  return (
    <Tooltip
      placement="left"
      title={getDisabledChainTooltipText(Boolean(disabled))}
    >
      <FormControlLabel
        classes={classes}
        control={
          <Checkbox
            disabled={isLoading || disabled}
            className={checkBoxClassName}
            checked={isChecked}
            onClick={onChangeChild}
          />
        }
        label={label}
      />
    </Tooltip>
  );
};
